/**
 * Conciliação bancária — B1: importação de extratos (mockups C1, C2, C3 e F12b aprovados; D-C2 a D-C6).
 *  - Conta e mês; saldo do extrato × saldo calculado × saldo disponível, cada um com a sua data; pendências por tipo.
 *  - Importar extrato (OFX preferencial, CSV alternativo) com PRÉVIA: conta conferida (OFX divergente bloqueia; CSV
 *    pede confirmação), corte do saldo inicial, novos · já importados · prováveis (conferência um a um) · contemplados
 *    no saldo inicial; identificador com dados divergentes = recusa explicada.
 *  - PDF do extrato só como evidência do mês.
 *  - Cancelar importação (evento vinculado, nada apagado): prévia do efeito, justificativa e confirmação; movimentos
 *    cancelados ficam visíveis ("Cancelados") e saem do saldo calculado e das pendências.
 *  - Rendimento informado pelo banco: só informação (nunca vira movimento); a diferença de saldo aparece no cartão.
 *  - OFX sem movimento novo com saldo do extrato: registra saldo e rastreio (A1); a prévia diz o que será registrado.
 *  - Filtro "Cancelados" preservado ao trocar de mês; sem resultado, "Mostrar todos" (A2).
 *  - Importar não cria despesa, pagamento nem receita.
 *  - B2: conciliar (sugestões em até 15 dias por valor exato, busca, 1↔N, N↔1, N↔N com soma conferida — C4, C4-2, C4-3),
 *    registrar pagamento e conciliar (C5), sem correspondência → despesa vinculada (F16), desfazer com justificativa
 *    (C6), entrada a identificar com observação e transferência entre contas só com as duas pontas (C7). Mês fechado:
 *    conciliar e desfazer bloqueados até reabrir.
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { Ajuda, CardLista, CardsLista, Codigo, ComCodigos, EstadoCarga, Modal, RolagemIndicada, SeloSituacao, useCarga, type Tom } from '../comercial/ui.tsx';
import { arquivoParaApi } from '../comercial/Pedidos.tsx';
import { BuscaFavorecido, dataBr, MEIOS, mesExtenso, novaChave, plural, rs, TIPOS_DOCUMENTO, useApoio, type Favorecido } from './ContasPagar.tsx';
import { Campo } from '../componentes/basicos.tsx';
import { ConciliarEntradas, IdentificarEntrada, RecuperarCredito, RegistrarRecebimentoConciliar } from './ConciliacaoEntradas.tsx';
import { ConciliarAplicacao } from './Aplicacoes.tsx';
import { navegar } from '../rotas.ts';

interface Conta { id: string; banco: string; agencia: string; numero: string; principal: boolean; saldoInicial: string | null; dataSaldoInicial: string | null; importadoAte: string | null; movimentos: number }
interface Movimento {
  numero: string; data: string; valor: string; identificador: string | null; descricao: string; natureza: Natureza; ativo: boolean; situacao: string; arquivos: number; importacao: string; canceladas: string | null; conciliacao: string | null; observacao: string | null;
  /** 0051 */
  conciliacaoTipo: string | null; pendenteVinculo: boolean; parProvavel: { tipo: 'rbm' | 'rcp'; numero: string } | null; ambigua: number | null;
}
type Natureza = 'saida' | 'entrada' | 'aplicacao_resgate' | 'transferencia';
interface Mes {
  conta: { id: string; banco: string; agencia: string; numero: string; saldoInicial: string | null; dataSaldoInicial: string | null };
  mes: string;
  saldos: {
    extrato: { valor: string; data: string; importacao: string } | null;
    calculado: { valor: string; data: string } | null;
    confere: boolean | null;
    diferenca: string | null;
    disponivel: { valor: string; data: string } | null;
    rendimento: { valor: string; rotulo: string; importacao: string } | null;
  };
  pendencias: Record<Natureza, number> & { total: number; paresProvaveis: number; ambiguas: number; pendentesVinculo: number };
  cancelados: number;
  conciliados: number;
  periodoFechado: boolean;
  movimentos: Movimento[];
  importacoes: {
    numero: string;
    formato: string;
    arquivo: string;
    periodo: { inicio: string; fim: string };
    linhas: { total: number; novas: number; jaImportadas: number; provaveisMesmo: number; corte: number };
    saldoExtrato: { valor: string; data: string } | null;
    rendimento: { valor: string; rotulo: string } | null;
    em: string;
    por: string | null;
    cancelamento: { em: string; por: string | null; justificativa: string; movimentosCancelados: number; movimentosMantidos: number } | null;
  }[];
  evidencias: { id: string; arquivo: string; tamanho: number; observacao: string | null; em: string; por: string | null }[];
}
interface LinhaPrevia { linha: number; data: string; valor: string; descricao: string; identificador: string | null; situacao: 'novo' | 'ja_importado' | 'provavel' | 'corte' | 'divergente' | 'repetido_no_arquivo'; natureza: Natureza; movimento: string | null; divergencia: string | null; decisao: 'mesmo' | 'novo' | null }
interface Previa {
  arquivo: { nome: string; formato: 'ofx' | 'csv'; perfil: string; impressao: string; jaImportadoEm: { numero: string; em: string } | null; importacoesCanceladas: string[] };
  conta: { id: string; banco: string; agencia: string; numero: string };
  contaNoArquivo: { agencia: string; numero: string } | null;
  contaConfere: boolean | null;
  exigeConfirmacaoConta: boolean;
  contaConfirmada: boolean;
  periodo: { inicio: string; fim: string };
  corte: { saldoInicial: string; data: string };
  saldos: { extrato: { valor: string; data: string } | null; disponivel: { valor: string; data: string } | null; rendimento: { valor: string; rotulo: string } | null };
  avisos: string[];
  registro: { movimentosNovos: number; movimentosExistentes: number; saldo: { valor: string; data: string } | null; saldoJaRegistradoEm: string | null; somenteSaldo: boolean };
  resumo: { total: number; novos: number; jaImportados: number; provaveis: number; provaveisSemDecisao: number; provaveisMesmo: number; corte: number; divergentes: number };
  linhas: LinhaPrevia[];
  bloqueios: { codigo: string; mensagem: string }[];
  pendencias: string[];
  podeImportar: boolean;
}

const NATUREZA: Record<Natureza, { rotulo: string; filtro: string }> = {
  saida: { rotulo: 'Saída · pendente de conciliação', filtro: 'Saídas' },
  entrada: { rotulo: 'Entrada · a identificar', filtro: 'Entradas' },
  aplicacao_resgate: { rotulo: 'Aplicação/Resgate · pendente', filtro: 'Aplicações e resgates' },
  transferencia: { rotulo: 'Transferência · pendente', filtro: 'Transferências' },
};
const SITUACAO_PREVIA: Record<LinhaPrevia['situacao'], { texto: string; tom: Tom }> = {
  novo: { texto: 'Novo', tom: 'positivo' },
  ja_importado: { texto: 'Já importado', tom: 'encerrado' },
  provavel: { texto: 'Provável já importado', tom: 'parcial' },
  corte: { texto: 'Contemplado no saldo inicial', tom: 'aberto' },
  divergente: { texto: 'Identificador com dados divergentes', tom: 'alerta' },
  repetido_no_arquivo: { texto: 'Identificador repetido no arquivo', tom: 'alerta' },
};
const valorColorido = (v: string) => (
  <span className={Number(v) < 0 ? 'conc-saida' : 'conc-entrada'}>
    {Number(v) > 0 ? '+' : ''}
    {rs(v)}
  </span>
);
const mesAtual = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' }).slice(0, 7);
/**
 * E0 (M3): meses da conta, do mês do dia seguinte ao saldo inicial (o corte da importação parte dele) até o mês atual —
 * nunca uma quantidade fixa nem uma data no código. Meses sem movimento no meio continuam na lista.
 */
const mesesDaConta = (c: Conta | undefined, atual: string) => {
  const inicio = c?.dataSaldoInicial ? new Date(Date.parse(`${c.dataSaldoInicial}T12:00:00Z`) + 86_400_000).toISOString().slice(0, 7) : atual;
  const out: string[] = [];
  for (let m = atual; m >= inicio && out.length < 600; ) {
    out.push(m);
    const [a, mm] = m.split('-').map(Number);
    m = new Date(Date.UTC(a, mm - 2, 1)).toISOString().slice(0, 7);
  }
  return out.length ? out : [atual];
};
interface EfeitoCancelamento {
  importacao: { numero: string; arquivo: string; formato: string; periodo: { inicio: string; fim: string }; em: string };
  cancelada: { em: string; por: string | null; justificativa: string } | null;
  cancelados: { numero: string; data: string; valor: string; descricao: string; identificador: string | null }[];
  mantidos: { numero: string; data: string; valor: string; descricao: string; identificador: string | null; sustentadoPor: string }[];
  efeitoSaldoCalculado: string;
  primeiraData: string | null;
  referencia: { mes: string; usada: boolean; substituta: { numero: string; valor: string; data: string } | null } | null;
  conciliados: number;
  conciliadosLista: { numero: string; data: string; valor: string; descricao: string; conciliacao: string; vinculo: string | null }[];
}
type Filtro = Natureza | 'todos' | 'cancelados' | 'conciliados';
const contaTexto = (c: { banco: string; agencia: string; numero: string }) => `${c.banco} · ag. ${c.agencia} · conta ${c.numero}`;

export function TelaConciliacao() {
  const { pode } = useSessao();
  const contas = useCarga(() => api.get<Conta[]>('/api/conciliacao/contas'), []);
  const [contaId, setContaId] = useState('');
  const [mes, setMes] = useState(mesAtual());
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [importar, setImportar] = useState(false);
  const [cancelar, setCancelar] = useState<string | null>(null);
  // B2
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [conciliar, setConciliar] = useState<string[] | null>(null);
  const [registrar, setRegistrar] = useState<string | null>(null);
  const [vinculada, setVinculada] = useState<string | null>(null);
  const [detalhe, setDetalhe] = useState<string | null>(null);
  const [verConciliacao, setVerConciliacao] = useState<{ conciliacao: string; movimento: string } | null>(null);
  // 0051: entradas
  const [conciliarEntrada, setConciliarEntrada] = useState<string[] | null>(null);
  const [registrarRecebimento, setRegistrarRecebimento] = useState<string | null>(null);
  const [identificar, setIdentificar] = useState<string | null>(null);
  const [recuperar, setRecuperar] = useState<{ movimento: string; identificacao?: string } | null>(null);
  const [pdf, setPdf] = useState(false);
  // 0055: aplicações e resgates × eventos da aplicação
  const [conciliarAplicacao, setConciliarAplicacao] = useState<string[] | null>(null);
  useEffect(() => {
    if (!contaId && contas.dados?.length) {
      const c = contas.dados[0];
      setContaId(c.id);
      if (c.importadoAte) setMes(c.importadoAte.slice(0, 7));
    }
  }, [contas.dados, contaId]);
  const dados = useCarga(() => (contaId ? api.get<Mes>(`/api/conciliacao/movimentos?conta=${contaId}&mes=${mes}`) : Promise.resolve(null)), [contaId, mes]);
  const d = dados.dados;
  // "Todos" e os tipos mostram só os ativos; os cancelados ficam no filtro próprio (histórico preservado);
  // os tipos mostram as PENDÊNCIAS (não conciliados); "Conciliados" mostra os vinculados
  const lista = useMemo(
    () =>
      d
        ? d.movimentos.filter((m) =>
            filtro === 'cancelados' ? !m.ativo : filtro === 'conciliados' ? m.ativo && Boolean(m.conciliacao) : m.ativo && (filtro === 'todos' || (m.natureza === filtro && !m.conciliacao)),
          )
        : [],
    [d, filtro],
  );
  const podeImportar = pode('importar_extrato', 'conciliacao');
  const podeConciliar = pode('conciliar', 'conciliacao');
  useEffect(() => setSelecionados([]), [contaId, mes]);
  // seleção múltipla: só saídas, só entradas OU só aplicações/resgates (cada uma com a sua janela de conciliação)
  const naturezaSelecionada = d?.movimentos.find((m) => selecionados.includes(m.numero))?.natureza;
  const selecionavel = (m: Movimento) => podeConciliar && !d?.periodoFechado && m.ativo && !m.conciliacao && (m.natureza === 'saida' || m.natureza === 'entrada' || m.natureza === 'aplicacao_resgate') && (!naturezaSelecionada || naturezaSelecionada === m.natureza);
  if (!contas.dados) return <EstadoCarga carregando={contas.carregando} erro={contas.erro} recarregar={contas.recarregar} teste="conciliacao" />;
  if (!contas.dados.length) return <p className="aviso aviso-atencao-fin">Nenhuma conta bancária ativa. Cadastre a conta da Inttrix (com saldo inicial e data de referência) em Cadastros base › Contas bancárias.</p>;
  const pend = d?.pendencias;
  const recarregar = () => (
    setImportar(false), setPdf(false), setCancelar(null), setConciliar(null), setRegistrar(null), setVinculada(null), setDetalhe(null), setVerConciliacao(null), setSelecionados([]),
    setConciliarEntrada(null), setRegistrarRecebimento(null), setIdentificar(null), setRecuperar(null), setConciliarAplicacao(null),
    void dados.recarregar(), void contas.recarregar()
  );
  const rendimentoTexto = d?.saldos.rendimento && Number(d.saldos.rendimento.valor) !== 0 ? ` · o banco informou rendimento de ${rs(d.saldos.rendimento.valor)}, não lançado como movimento` : '';
  return (
    <>
      <h1 className="titulo-pagina rotulo-oculto">Conciliação bancária</h1>
      {d && (
        <CardsLista n={4} teste="cards-conciliacao">
          <CardLista
            icone="documento"
            tom="vermelho"
            rotulo="Saldo do extrato"
            valor={d.saldos.extrato ? rs(d.saldos.extrato.valor) : '—'}
            detalhe={d.saldos.extrato ? `em ${dataBr(d.saldos.extrato.data)} · usado na conferência` : 'não informado neste mês'}
            ajuda={<Ajuda titulo="Saldo do extrato">Saldo informado pelo banco no arquivo importado (no OFX, o saldo do extrato), na data dele — a posição ao fim daquele dia. É o único comparado com o saldo calculado, sempre na mesma data.</Ajuda>}
            teste="card-saldo-extrato"
          />
          <CardLista
            icone="relogio"
            tom="roxo"
            rotulo="Saldo calculado"
            valor={d.saldos.calculado ? rs(d.saldos.calculado.valor) : '—'}
            detalhe={d.saldos.calculado ? `em ${dataBr(d.saldos.calculado.data)} · ${d.saldos.confere === null ? 'sem saldo do extrato para conferir' : d.saldos.confere ? 'confere com o extrato ✓' : `diferença ${rs(d.saldos.diferenca!)} (extrato − calculado)`}${rendimentoTexto}` : 'conta sem saldo inicial'}
            ajuda={<Ajuda titulo="Saldo calculado">Saldo inicial da conta (posição ao fim da data de referência) mais os movimentos ATIVOS importados até a data do saldo do extrato — movimentos de importações canceladas não entram. Diferença zero não quer dizer que o mês esteja completo. O rendimento informado pelo banco é só informação: nunca é somado nem vira ajuste; se o saldo do extrato o incluir sem lançamento, a diferença aparece aqui.</Ajuda>}
            teste="card-saldo-calculado"
          />
          <CardLista
            icone="dinheiro"
            tom="azul"
            rotulo="Saldo disponível"
            valor={d.saldos.disponivel ? rs(d.saldos.disponivel.valor) : '—'}
            detalhe={d.saldos.disponivel ? `em ${dataBr(d.saldos.disponivel.data)} · só informação` : 'não informado no arquivo'}
            ajuda={<Ajuda titulo="Saldo disponível">Quando o banco informa, pode ser diferente do saldo do extrato (valores bloqueados ou a compensar). É só informação e nunca substitui o saldo do extrato na conferência.</Ajuda>}
            teste="card-saldo-disponivel"
          />
          <CardLista
            icone="alerta"
            tom="verde"
            rotulo="Pendências do mês"
            valor={plural(pend!.total, 'movimento', 'movimentos')}
            detalhe={`${plural(pend!.saida, 'saída', 'saídas')} · ${plural(pend!.entrada, 'entrada', 'entradas')}${pend!.paresProvaveis ? ` (${pend!.paresProvaveis} com par provável)` : ''}${pend!.ambiguas ? ` (${plural(pend!.ambiguas, 'ambígua', 'ambíguas')})` : ''}${pend!.pendentesVinculo ? ` · ${plural(pend!.pendentesVinculo, 'devolução pendente de vínculo', 'devoluções pendentes de vínculo')}` : ''} · ${plural(pend!.aplicacao_resgate, 'aplicação/resgate', 'aplicações/resgates')} · ${plural(pend!.transferencia, 'transferência', 'transferências')}`}
            ajuda={<Ajuda titulo="Pendências do mês">Movimentos importados ainda não conciliados, por tipo. Saídas: vincule ao pagamento (ou registre na hora). Entradas: concilie com o recebimento ou a recuperação já registrados, registre e concilie numa operação só, ou identifique a natureza — nada vira receita automaticamente. “Par provável” é só sugestão (correspondência única); “ambígua” tem mais de um candidato e continua pendente. Aplicações e resgates se conciliam com os eventos da aplicação (Aplicações): o dinheiro conta uma vez.</Ajuda>}
            teste="card-pendencias"
          />
        </CardsLista>
      )}
      <div className="barra-filtros conc-barra" data-teste="filtros-conciliacao">
        <label>
          Conta
          <select value={contaId} onChange={(e) => setContaId(e.target.value)} data-teste="conta-conciliacao">
            {contas.dados.map((c) => (
              <option key={c.id} value={c.id}>
                {contaTexto(c)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Mês
          <select value={mes} onChange={(e) => setMes(e.target.value)} data-teste="mes-conciliacao">
            {[...new Set([...mesesDaConta(contas.dados?.find((c) => c.id === contaId), mesAtual()), mes])].sort().reverse().map((m) => (
              <option key={m} value={m}>
                {mesExtenso(m)}
              </option>
            ))}
          </select>
        </label>
        <span className="conc-espaco" />
        <button type="button" className="botao-secundario" onClick={() => navegar('/aplicacoes')} data-teste="ir-aplicacoes">
          Aplicações
        </button>
        {podeImportar && (
          <>
            <button type="button" className="botao-secundario" onClick={() => setPdf(true)} data-teste="anexar-pdf-extrato">
              Anexar PDF do extrato
            </button>
            <button type="button" className="botao-primario" onClick={() => setImportar(true)} data-teste="importar-extrato">
              Importar extrato (OFX ou CSV)
            </button>
          </>
        )}
      </div>
      {!d ? (
        <EstadoCarga carregando={dados.carregando} erro={dados.erro} recarregar={dados.recarregar} teste="movimentos-conciliacao" />
      ) : (
        <>
          <h2 className="conc-titulo">
            Movimentos de {mesExtenso(mes)}
            <Ajuda titulo="Movimentos do extrato">
              Importar o extrato não cria despesa, receita nem pagamento: os movimentos do banco ficam pendentes até a conciliação, que vincula cada movimento ao registro que já existe (etapa B2). Compras no cartão e pagamentos feitos por pessoa não aparecem aqui — a saída da conta é o pagamento da fatura ou do reembolso.
            </Ajuda>
          </h2>
          {d.periodoFechado && (
            <p className="aviso aviso-atencao-fin" data-teste="periodo-fechado-conciliacao">
              <strong>{mesExtenso(mes)} está fechado.</strong> Importar o extrato continua possível; conciliar e desfazer exigem reabrir o período (Contas a Pagar › Períodos financeiros).
            </p>
          )}
          <div className="chips-estados" role="group" aria-label="Filtrar movimentos" data-teste="chips-conciliacao">
            {(['todos', 'saida', 'entrada', 'aplicacao_resgate', 'transferencia', ...(d.conciliados || filtro === 'conciliados' ? (['conciliados'] as const) : []), ...(d.cancelados || filtro === 'cancelados' ? (['cancelados'] as const) : [])] as const).map((k) => (
              <button key={k} type="button" className={`chip${filtro === k ? ' ativo' : ''}`} aria-pressed={filtro === k} onClick={() => setFiltro(k)} data-teste={`chip-${k}`}>
                {k === 'todos' ? `Todos (${d.pendencias.total + d.conciliados})` : k === 'cancelados' ? `Cancelados (${d.cancelados})` : k === 'conciliados' ? `Conciliados (${d.conciliados})` : `${NATUREZA[k].filtro} (${d.pendencias[k]})`}
              </button>
            ))}
          </div>
          {selecionados.length > 0 && (
            <div className="conc-barra-selecao" data-teste="barra-selecao">
              <span>
                {plural(selecionados.length, 'movimento selecionado', 'movimentos selecionados')} ·{' '}
                {rs(Math.abs(d.movimentos.filter((m) => selecionados.includes(m.numero)).reduce((s2, m) => s2 + Number(m.valor), 0)))}
              </span>
              <button type="button" className="botao-link" onClick={() => setSelecionados([])}>
                Limpar seleção
              </button>
              <button type="button" className="botao-primario" onClick={() => (naturezaSelecionada === 'entrada' ? setConciliarEntrada(selecionados) : naturezaSelecionada === 'aplicacao_resgate' ? setConciliarAplicacao(selecionados) : setConciliar(selecionados))} data-teste="conciliar-selecionados">
                Conciliar selecionados
              </button>
            </div>
          )}
          {lista.length ? (
            <RolagemIndicada className="rolagem quadro-tabela">
              <table className="tabela tabela-compacta" data-teste="tabela-movimentos">
                <thead>
                  <tr>
                    {podeConciliar && <th className="col-selecao" aria-label="Selecionar" />}
                    <th>Data</th>
                    <th>Descrição no banco · identificador · origem</th>
                    <th className="numero">Valor</th>
                    <th>Situação</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((m) => (
                    <tr key={m.numero} data-teste={`movimento-${m.numero}`} className={m.ativo ? undefined : 'conc-cancelado'}>
                      {podeConciliar && (
                        <td className="col-selecao">
                          {selecionavel(m) && (
                            <input
                              type="checkbox"
                              aria-label={`Selecionar ${m.numero}`}
                              checked={selecionados.includes(m.numero)}
                              onChange={(e) => setSelecionados(e.target.checked ? [...selecionados, m.numero] : selecionados.filter((x) => x !== m.numero))}
                              data-teste={`selecionar-${m.numero}`}
                            />
                          )}
                        </td>
                      )}
                      <td>{dataBr(m.data)}</td>
                      <td>
                        {m.descricao}
                        <div className="texto-secundario conc-mini">
                          {m.identificador ?? 'sem identificador do banco'} · <Codigo>{m.numero}</Codigo> · <ComCodigos texto={m.importacao} />
                          {m.arquivos > 1 && ` · visto em ${m.arquivos} arquivos`}
                        </div>
                      </td>
                      <td className="numero">{valorColorido(m.valor)}</td>
                      <td>
                        {!m.ativo ? (
                          <SeloSituacao texto={m.situacao} tom="encerrado" />
                        ) : m.conciliacao ? (
                          <SeloSituacao texto={m.situacao} tom={m.pendenteVinculo ? 'parcial' : 'positivo'} />
                        ) : (
                          <>
                            <span className="conc-pend">{NATUREZA[m.natureza].rotulo}</span>
                            {m.parProvavel && (
                              <div className="texto-secundario conc-mini" data-teste={`par-${m.numero}`}>
                                par provável: {m.parProvavel.numero} · sugestão, não conciliado
                              </div>
                            )}
                            {m.ambigua && (
                              <div className="texto-secundario conc-mini" data-teste={`ambigua-${m.numero}`}>
                                ambígua: {m.ambigua} candidatos de mesmo valor
                              </div>
                            )}
                          </>
                        )}
                        {m.observacao && <div className="texto-secundario conc-mini">Obs.: {m.observacao}</div>}
                      </td>
                      <td className="conc-acoes">
                        {m.ativo && m.conciliacao ? (
                          <button type="button" className="botao-link" onClick={() => setVerConciliacao({ conciliacao: m.conciliacao!, movimento: m.numero })} data-teste={`ver-conciliacao-${m.numero}`}>
                            Ver conciliação
                          </button>
                        ) : m.ativo && m.natureza === 'saida' && selecionavel(m) ? (
                          <button type="button" className="botao-link" onClick={() => setConciliar([m.numero])} data-teste={`conciliar-${m.numero}`}>
                            Conciliar
                          </button>
                        ) : m.ativo && m.natureza === 'entrada' && podeConciliar && !d.periodoFechado ? (
                          <span className="conc-acoes-pilha">
                            <button type="button" className="botao-link" onClick={() => setConciliarEntrada([m.numero])} data-teste={`conciliar-${m.numero}`}>
                              Conciliar…
                            </button>
                            <button type="button" className="botao-link" onClick={() => setIdentificar(m.numero)} data-teste={`identificar-${m.numero}`}>
                              Identificar
                            </button>
                            <button type="button" className="botao-link" onClick={() => setDetalhe(m.numero)} data-teste={`detalhe-${m.numero}`}>
                              Detalhes
                            </button>
                          </span>
                        ) : m.ativo && m.natureza === 'aplicacao_resgate' && podeConciliar && !d.periodoFechado ? (
                          <span className="conc-acoes-pilha">
                            <button type="button" className="botao-link" onClick={() => setConciliarAplicacao([m.numero])} data-teste={`conciliar-${m.numero}`}>
                              Conciliar…
                            </button>
                            <button type="button" className="botao-link" onClick={() => setDetalhe(m.numero)} data-teste={`detalhe-${m.numero}`}>
                              Detalhes
                            </button>
                          </span>
                        ) : m.ativo ? (
                          <button type="button" className="botao-link" onClick={() => setDetalhe(m.numero)} data-teste={`detalhe-${m.numero}`}>
                            Detalhes
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </RolagemIndicada>
          ) : (
            <p className="texto-secundario" data-teste="sem-movimentos">
              {filtro === 'conciliados' ? (
                'Nenhum movimento conciliado neste período.'
              ) : filtro === 'cancelados' ? (
                <>
                  Nenhum movimento cancelado neste período.{' '}
                  <button type="button" className="botao-link" onClick={() => setFiltro('todos')} data-teste="mostrar-todos">
                    Mostrar todos
                  </button>
                </>
              ) : (
                `Nenhum movimento importado ${filtro === 'todos' ? 'neste mês' : 'deste tipo neste mês'}.`
              )}
            </p>
          )}
          <h2 className="conc-titulo">Extratos e evidências do mês</h2>
          {d.importacoes.length || d.evidencias.length ? (
            <ul className="conc-lista" data-teste="importacoes-mes">
              {d.importacoes.map((i) => (
                <li key={i.numero} className={i.cancelamento ? 'conc-cancelado' : undefined} data-teste={`importacao-${i.numero}`}>
                  <strong>{i.numero}</strong> · {i.arquivo} ({i.formato.toUpperCase()}) · {dataBr(i.periodo.inicio)} a {dataBr(i.periodo.fim)} · {plural(i.linhas.novas, 'novo', 'novos')}, {i.linhas.jaImportadas} já importados, {i.linhas.provaveisMesmo} conferidos como o mesmo, {i.linhas.corte} no saldo inicial
                  {i.rendimento && <> · rendimento informado {rs(i.rendimento.valor)} (só informação)</>}
                  <span className="texto-secundario"> · {new Date(i.em).toLocaleString('pt-BR')}{i.por ? ` · ${i.por}` : ''}</span>
                  {i.cancelamento ? (
                    <div className="conc-cancelamento" data-teste={`cancelamento-${i.numero}`}>
                      <SeloSituacao texto="Cancelada" tom="encerrado" /> em {new Date(i.cancelamento.em).toLocaleString('pt-BR')}
                      {i.cancelamento.por ? ` por ${i.cancelamento.por}` : ''} · {plural(i.cancelamento.movimentosCancelados, 'movimento cancelado', 'movimentos cancelados')}, {plural(i.cancelamento.movimentosMantidos, 'mantido', 'mantidos')} por outro arquivo · “{i.cancelamento.justificativa}”
                    </div>
                  ) : (
                    podeImportar && (
                      <>
                        {' '}
                        <button type="button" className="botao-link" onClick={() => setCancelar(i.numero)} data-teste={`cancelar-${i.numero}`}>
                          Cancelar importação
                        </button>
                      </>
                    )
                  )}
                </li>
              ))}
              {d.evidencias.map((e) => (
                <li key={e.id}>
                  PDF do extrato:{' '}
                  <button type="button" className="botao-link" onClick={() => void baixarEvidencia(e.id)} data-teste={`evidencia-${e.arquivo}`}>
                    {e.arquivo}
                  </button>
                  {e.observacao && <span className="texto-secundario"> · {e.observacao}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="texto-secundario">Nenhum extrato importado nem PDF anexado neste mês.</p>
          )}
        </>
      )}
      {importar && contaId && <ImportarExtrato contaId={contaId} contas={contas.dados} aoFechar={() => setImportar(false)} aoImportar={recarregar} />}
      {pdf && contaId && <AnexarPdf contaId={contaId} mes={mes} aoFechar={() => setPdf(false)} aoAnexar={recarregar} />}
      {cancelar && <CancelarImportacao numero={cancelar} aoFechar={() => setCancelar(null)} aoCancelar={recarregar} />}
      {conciliar && (
        <ConciliarMovimentos
          movimentos={conciliar}
          aoFechar={() => setConciliar(null)}
          aoConciliar={recarregar}
          aoRegistrar={(n) => (setConciliar(null), setRegistrar(n))}
          aoVinculada={(n) => (setConciliar(null), setVinculada(n))}
          aoTransferencia={(n) => (setConciliar(null), setDetalhe(n))}
          aoDevolucao={(movs) => (setConciliar(null), setConciliarEntrada(movs))}
        />
      )}
      {registrar && <RegistrarEConciliar movimento={registrar} aoFechar={() => setRegistrar(null)} aoConcluir={recarregar} />}
      {vinculada && <DespesaVinculada movimento={vinculada} aoFechar={() => setVinculada(null)} aoConcluir={recarregar} />}
      {detalhe && <MovimentoDetalhe numero={detalhe} aoFechar={() => setDetalhe(null)} aoAlterar={recarregar} />}
      {verConciliacao && (
        <DesfazerConciliacao
          {...verConciliacao}
          aoFechar={() => setVerConciliacao(null)}
          aoDesfazer={recarregar}
          aoVincular={(identificacao) => (setVerConciliacao(null), setRecuperar({ movimento: verConciliacao.movimento, identificacao }))}
        />
      )}
      {conciliarEntrada && (
        <ConciliarEntradas
          movimentos={conciliarEntrada}
          aoFechar={() => setConciliarEntrada(null)}
          aoConcluir={recarregar}
          aoRegistrar={(n) => (setConciliarEntrada(null), setRegistrarRecebimento(n))}
          aoIdentificar={(n) => (setConciliarEntrada(null), setIdentificar(n))}
        />
      )}
      {registrarRecebimento && <RegistrarRecebimentoConciliar movimento={registrarRecebimento} aoFechar={() => setRegistrarRecebimento(null)} aoConcluir={recarregar} />}
      {identificar && (
        <IdentificarEntrada
          movimento={identificar}
          aoFechar={() => setIdentificar(null)}
          aoConcluir={recarregar}
          aoRecebimento={() => (setIdentificar(null), setRegistrarRecebimento(identificar))}
          aoDevolucao={() => (setIdentificar(null), setRecuperar({ movimento: identificar }))}
          aoTransferencia={() => (setIdentificar(null), setDetalhe(identificar))}
        />
      )}
      {conciliarAplicacao && <ConciliarAplicacao movimentos={conciliarAplicacao} aoFechar={() => setConciliarAplicacao(null)} aoConcluir={recarregar} />}
      {recuperar && <RecuperarCredito movimento={recuperar.movimento} identificacao={recuperar.identificacao} aoFechar={() => setRecuperar(null)} aoConcluir={recarregar} />}
    </>
  );
}

async function baixarEvidencia(id: string) {
  const r = await api.get<{ nome: string; conteudo_base64: string }>(`/api/conciliacao/evidencias/${id}/arquivo`);
  const bytes = Uint8Array.from(atob(r.conteudo_base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
  const el = document.createElement('a');
  el.href = url;
  el.download = r.nome;
  el.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ---------- Importar extrato: prévia (C2), conta divergente (C3), prováveis (F12b) ---------- */
function ImportarExtrato({ contaId, contas, aoFechar, aoImportar }: { contaId: string; contas: Conta[]; aoFechar: () => void; aoImportar: () => void }) {
  const conta = contas.find((c) => c.id === contaId)!;
  const [arquivo, setArquivo] = useState<{ nome: string; conteudo_base64: string } | null>(null);
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [confirmada, setConfirmada] = useState(false);
  const [decisoes, setDecisoes] = useState<Record<number, 'mesmo' | 'novo'>>({});
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [feito, setFeito] = useState<{ importacao: string; resumo: Previa['resumo']; registro: Previa['registro'] } | null>(null);
  const [verLinhas, setVerLinhas] = useState(false);
  const corpo = () => ({ conta_bancaria_id: contaId, arquivo, conta_confirmada: confirmada, decisoes });
  useEffect(() => {
    if (!arquivo) return;
    let vivo = true;
    setErro('');
    api
      .post<Previa>('/api/conciliacao/importacoes/previa', corpo())
      .then((p) => vivo && setPrevia(p))
      .catch((e: Error) => vivo && (setPrevia(null), setErro(e.message)));
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arquivo, confirmada, decisoes]);
  const escolher = async (f: File | null) => {
    setPrevia(null);
    setDecisoes({});
    setConfirmada(false);
    setArquivo((await arquivoParaApi(f)) ?? null);
  };
  const importarAgora = async () => {
    setEnviando(true);
    setErro('');
    try {
      setFeito(await api.post<{ importacao: string; resumo: Previa['resumo']; registro: Previa['registro'] }>('/api/conciliacao/importacoes', corpo()));
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  const provaveis = previa?.linhas.filter((l) => l.situacao === 'provavel') ?? [];
  const decidirTodos = (d: 'mesmo' | 'novo') => setDecisoes(Object.fromEntries(provaveis.map((l) => [l.linha, d])));
  const titulo = 'Importar extrato — prévia';
  if (feito)
    return (
      <Modal titulo={titulo} aoFechar={aoImportar} medio rodape={<button type="button" className="botao-primario" onClick={aoImportar} data-teste="concluir-importacao">Concluir</button>}>
        <p className="aviso aviso-sucesso" role="status" data-teste="importacao-concluida">
          <strong>{feito.importacao}</strong> importado: {plural(feito.resumo.novos, 'movimento novo', 'movimentos novos')}, {plural(feito.resumo.jaImportados, 'já importado', 'já importados')}, {plural(feito.resumo.provaveisMesmo, 'conferido como o mesmo', 'conferidos como o mesmo')}, {plural(feito.resumo.corte, 'contemplado', 'contemplados')} no saldo inicial.
          {feito.registro.saldo && <> Saldo do extrato registrado para a conferência: {rs(feito.registro.saldo.valor)} em {dataBr(feito.registro.saldo.data)}.</>} Nenhuma despesa, pagamento ou receita foi criado.
        </p>
      </Modal>
    );
  const r = previa?.resumo;
  return (
    <Modal
      titulo={titulo}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!previa?.podeImportar || enviando} onClick={() => void importarAgora()} data-teste="confirmar-importacao">
            {previa?.registro.somenteSaldo ? 'Registrar saldo e rastreio' : r ? `Importar ${plural(r.novos, 'movimento', 'movimentos')}` : 'Importar'}
          </button>
        </>
      }
    >
      <div className="campo">
        <label htmlFor="arquivo-extrato">
          Arquivo do extrato (OFX ou CSV do Nubank) · conta {contaTexto(conta)}
          <Ajuda titulo="Formatos">OFX é o formato preferencial (traz conta, período e saldo do extrato). O CSV não traz conta nem saldo: pede a confirmação da conta. O PDF é só evidência — use “Anexar PDF do extrato”. Por enquanto só extratos do Nubank foram validados.</Ajuda>
        </label>
        <input id="arquivo-extrato" type="file" accept=".ofx,.csv" onChange={(e) => void escolher(e.target.files?.[0] ?? null)} data-teste="arquivo-extrato" />
      </div>
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-importacao">
          {erro}
        </p>
      )}
      {previa && (
        <div data-teste="previa-importacao">
          <p className="faixa-resumo-modal">
            <span>
              <strong>{previa.arquivo.nome}</strong> · {previa.arquivo.formato.toUpperCase()} · perfil Nubank
            </span>
            <span>
              Período do arquivo: {dataBr(previa.periodo.inicio)} a {dataBr(previa.periodo.fim)}
            </span>
          </p>
          {previa.arquivo.jaImportadoEm && (
            <p className="aviso aviso-atencao-fin" data-teste="arquivo-ja-importado">
              Este mesmo arquivo já foi importado em {previa.arquivo.jaImportadoEm.numero} ({new Date(previa.arquivo.jaImportadoEm.em).toLocaleString('pt-BR')}).
            </p>
          )}
          {previa.arquivo.importacoesCanceladas.length > 0 && !previa.arquivo.jaImportadoEm && (
            <p className="aviso aviso-atencao-fin" data-teste="arquivo-cancelado-antes">
              Este mesmo arquivo foi importado antes em {previa.arquivo.importacoesCanceladas.join(', ')}, que {previa.arquivo.importacoesCanceladas.length === 1 ? 'foi cancelada' : 'foram canceladas'}. Reimportar é permitido: só entra o que não estiver ativo.
            </p>
          )}
          {previa.bloqueios.map((b) => (
            <p key={b.mensagem} className="aviso aviso-erro" role="alert" data-teste={`bloqueio-${b.codigo}`}>
              {b.codigo === 'conta_divergente' ? b.mensagem : <><strong>Importação recusada.</strong> {b.mensagem}</>}
            </p>
          ))}
          <div className="conc-grade2">
            <div className="conc-caixa">
              <h3>
                Conta
                <Ajuda titulo="Conta do arquivo">A conta do arquivo precisa ser a conta escolhida. Agência e número são comparados sem zeros à esquerda e sem separadores. O HUB nunca troca a conta do arquivo.</Ajuda>
              </h3>
              {previa.contaNoArquivo ? (
                <>
                  <div className="texto-secundario">
                    No arquivo: ag. {previa.contaNoArquivo.agencia} · conta {previa.contaNoArquivo.numero}
                  </div>
                  <div className={previa.contaConfere ? 'conc-ok' : 'conc-dif'} data-teste="conta-confere">
                    {previa.contaConfere ? `✓ Confere com ${previa.conta.banco}` : '✗ Não confere com a conta escolhida'}
                  </div>
                </>
              ) : (
                <label className="conc-confirmar" data-teste="confirmar-conta-csv">
                  <input type="checkbox" checked={confirmada} onChange={(e) => setConfirmada(e.target.checked)} />
                  <span>
                    O CSV não traz a conta. <strong>Confirmo que este arquivo é da conta {contaTexto(previa.conta)}.</strong>
                  </span>
                </label>
              )}
            </div>
            <div className="conc-caixa">
              <h3>
                Saldos do arquivo
                <Ajuda titulo="Saldos">Só o saldo do extrato entra na conferência, comparado ao saldo calculado na mesma data. O saldo disponível pode ser diferente (valores bloqueados ou a compensar) e é só informação.</Ajuda>
              </h3>
              <div data-teste="saldo-extrato-previa">
                <strong>Saldo do extrato:</strong> {previa.saldos.extrato ? `${rs(previa.saldos.extrato.valor)} em ${dataBr(previa.saldos.extrato.data)}` : 'não informado no arquivo'}
              </div>
              <div data-teste="saldo-disponivel-previa">
                <strong>Saldo disponível:</strong> {previa.saldos.disponivel ? `${rs(previa.saldos.disponivel.valor)} em ${dataBr(previa.saldos.disponivel.data)}` : 'não informado no arquivo'}
              </div>
              <div data-teste="rendimento-previa">
                <strong>Rendimento informado:</strong> {previa.saldos.rendimento ? `${rs(previa.saldos.rendimento.valor)} no período · só informação, não vira movimento` : 'não informado no arquivo'}
              </div>
            </div>
          </div>
          {!previa.bloqueios.length && (
            <div className={`conc-caixa${previa.registro.somenteSaldo ? ' conc-registro-saldo' : ''}`} data-teste="registro-previa">
              <h3>
                O que será registrado
                <Ajuda titulo="Saldo sem movimento novo">Um OFX cujos movimentos já existem (por exemplo, depois do CSV do mesmo período, ou ao reimportar um extrato cancelado) ainda pode ser importado para registrar o saldo do extrato e o rastreio do arquivo. Nenhum movimento é duplicado. Se o mesmo saldo já estiver registrado, não há nada novo; se outro arquivo ativo registrou um saldo diferente na mesma data, a importação é recusada e a referência não muda.</Ajuda>
              </h3>
              <div>
                <strong data-teste="registro-novos">{plural(previa.registro.movimentosNovos, 'movimento novo', 'movimentos novos')}</strong> ·{' '}
                <span data-teste="registro-existentes">{plural(previa.registro.movimentosExistentes, 'movimento já existente', 'movimentos já existentes')} (só rastreio)</span>
              </div>
              <div data-teste="registro-saldo">
                {previa.registro.saldo ? (
                  <>
                    Saldo do extrato que será registrado: <strong>{rs(previa.registro.saldo.valor)}</strong> em {dataBr(previa.registro.saldo.data)}
                  </>
                ) : previa.registro.saldoJaRegistradoEm ? (
                  `Saldo do extrato já registrado em ${previa.registro.saldoJaRegistradoEm} — não será registrado de novo`
                ) : (
                  'Nenhum saldo do extrato será registrado (o arquivo não informa)'
                )}
              </div>
            </div>
          )}
          {previa.avisos.map((a) => (
            <p key={a} className="aviso aviso-atencao-fin" data-teste="aviso-rendimento">
              {a}
            </p>
          ))}
          <p className="aviso aviso-atencao-fin" data-teste="corte-saldo-inicial">
            <strong>Corte do saldo inicial:</strong> o saldo inicial da conta ({rs(previa.corte.saldoInicial)}) é a posição <strong>ao fim do dia {dataBr(previa.corte.data)}</strong> e já contempla todos os movimentos até essa data, inclusive. Entram movimentos <strong>a partir do dia seguinte</strong>.
          </p>
          {r && (
            <RolagemIndicada className="rolagem quadro-tabela">
              <table className="tabela tabela-compacta" data-teste="resumo-previa">
                <thead>
                  <tr>
                    <th>Resultado</th>
                    <th className="numero">Linhas</th>
                    <th>O que acontece</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><SeloSituacao texto="Novos" tom="positivo" /></td>
                    <td className="numero">{r.novos}</td>
                    <td>entram como movimentos do banco (nada é criado no Contas a Pagar)</td>
                  </tr>
                  <tr>
                    <td><SeloSituacao texto="Já importados" tom="encerrado" /></td>
                    <td className="numero">{r.jaImportados}</td>
                    <td>mesmo identificador do banco nesta conta — não duplicam; o arquivo fica no rastreio</td>
                  </tr>
                  <tr>
                    <td><SeloSituacao texto="Prováveis já importados" tom="parcial" /></td>
                    <td className="numero">{r.provaveis}</td>
                    <td>sem identificador e mesma contagem de data, valor e descrição — conferir abaixo</td>
                  </tr>
                  <tr>
                    <td><SeloSituacao texto="Contemplados no saldo inicial" tom="aberto" /></td>
                    <td className="numero">{r.corte}</td>
                    <td>até {dataBr(previa.corte.data)} — contados, não importados</td>
                  </tr>
                  {r.divergentes > 0 && (
                    <tr>
                      <td><SeloSituacao texto="Dados divergentes" tom="alerta" /></td>
                      <td className="numero">{r.divergentes}</td>
                      <td>identificador já existente com outros dados — a importação é recusada</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </RolagemIndicada>
          )}
          {r && (
            <p className="faixa-resumo-modal" data-teste="total-previa">
              <span>
                Total do arquivo <strong>{r.total}</strong> = {plural(r.novos - provaveis.filter((l) => l.decisao === 'novo').length, 'novo', 'novos')} + {plural(r.jaImportados, 'já importado', 'já importados')} + {plural(r.provaveis, 'provável', 'prováveis')} + {r.corte} no saldo inicial
                {r.divergentes ? ` + ${plural(r.divergentes, 'divergente', 'divergentes')}` : ''}
                <Ajuda titulo="Nenhuma linha some">Nenhuma linha é descartada em silêncio. Movimentos iguais legítimos (mesma data, valor e descrição, com identificadores diferentes) são preservados.</Ajuda>
              </span>
            </p>
          )}
          {provaveis.length > 0 && (
            <div className="conc-caixa" data-teste="provaveis">
              <h3>
                Prováveis já importados — conferir ({previa.resumo.provaveisSemDecisao} sem decisão)
                <Ajuda titulo="Sem identificador">Sem identificador do banco, a mesma quantidade de linhas com a mesma data, valor e descrição sugere que já foram importadas — mas não prova. Decida cada uma: “é o mesmo” (não duplica) ou “é novo” (entra).</Ajuda>
              </h3>
              <div className="conc-acoes-linha">
                <button type="button" className="botao-secundario pequeno" onClick={() => decidirTodos('mesmo')} data-teste="todos-mesmo">
                  Todos são os mesmos
                </button>
                <button type="button" className="botao-secundario pequeno" onClick={() => decidirTodos('novo')} data-teste="todos-novos">
                  Todos são novos
                </button>
              </div>
              {provaveis.map((l) => (
                <div key={l.linha} className="conc-provavel" data-teste={`provavel-${l.linha}`}>
                  <span>
                    Linha {l.linha} · {dataBr(l.data)} · {valorColorido(l.valor)} · {l.descricao}
                  </span>
                  <label>
                    <input type="radio" name={`dec-${l.linha}`} checked={decisoes[l.linha] === 'mesmo'} onChange={() => setDecisoes({ ...decisoes, [l.linha]: 'mesmo' })} /> é o mesmo
                  </label>
                  <label>
                    <input type="radio" name={`dec-${l.linha}`} checked={decisoes[l.linha] === 'novo'} onChange={() => setDecisoes({ ...decisoes, [l.linha]: 'novo' })} /> é novo
                  </label>
                </div>
              ))}
            </div>
          )}
          {previa.pendencias.length > 0 && !previa.bloqueios.length && (
            <ul className="conc-pendencias" data-teste="pendencias-previa">
              {previa.pendencias.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
          <button type="button" className="botao-link" onClick={() => setVerLinhas(!verLinhas)} data-teste="ver-linhas">
            {verLinhas ? 'Ocultar as linhas do arquivo' : previa.linhas.length === 1 ? 'Ver a linha do arquivo' : `Ver as ${previa.linhas.length} linhas do arquivo`}
          </button>
          {verLinhas && (
            <RolagemIndicada className="rolagem quadro-tabela">
              <table className="tabela tabela-compacta" data-teste="linhas-previa">
                <thead>
                  <tr>
                    <th>Linha</th>
                    <th>Data</th>
                    <th>Descrição</th>
                    <th className="numero">Valor</th>
                    <th>Resultado</th>
                  </tr>
                </thead>
                <tbody>
                  {previa.linhas.map((l) => (
                    <tr key={l.linha}>
                      <td>{l.linha}</td>
                      <td>{dataBr(l.data)}</td>
                      <td>
                        {l.descricao}
                        <div className="texto-secundario conc-mini">{l.identificador ?? 'sem identificador'}</div>
                      </td>
                      <td className="numero">{valorColorido(l.valor)}</td>
                      <td>
                        <SeloSituacao texto={SITUACAO_PREVIA[l.situacao].texto} tom={SITUACAO_PREVIA[l.situacao].tom} />
                        {l.movimento && <div className="texto-secundario conc-mini">{l.movimento}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </RolagemIndicada>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ---------- Cancelar importação: evento vinculado, nada apagado ---------- */
function CancelarImportacao({ numero, aoFechar, aoCancelar }: { numero: string; aoFechar: () => void; aoCancelar: () => void }) {
  const efeito = useCarga(() => api.get<EfeitoCancelamento>(`/api/conciliacao/importacoes/${numero}/cancelamento`), [numero]);
  const [justificativa, setJustificativa] = useState('');
  const [confirmado, setConfirmado] = useState(false);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const e = efeito.dados;
  const enviar = async () => {
    setEnviando(true);
    setErro('');
    try {
      await api.post(`/api/conciliacao/importacoes/${numero}/cancelamento`, { justificativa, confirmado });
      aoCancelar();
    } catch (x) {
      setErro((x as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  const bloqueado = Boolean(e && e.conciliados > 0);
  const pronto = Boolean(e && !e.cancelada && !bloqueado && justificativa.trim().length >= 10 && confirmado);
  return (
    <Modal
      titulo={`Cancelar importação ${numero}`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!pronto || enviando} onClick={() => void enviar()} data-teste="confirmar-cancelamento">
            Cancelar importação
          </button>
        </>
      }
    >
      {!e ? (
        <EstadoCarga carregando={efeito.carregando} erro={efeito.erro} recarregar={efeito.recarregar} teste="efeito-cancelamento" />
      ) : (
        <div data-teste="previa-cancelamento">
          <p className="faixa-resumo-modal">
            <span>
              <strong>{e.importacao.arquivo}</strong> · {e.importacao.formato.toUpperCase()} · {dataBr(e.importacao.periodo.inicio)} a {dataBr(e.importacao.periodo.fim)}
            </span>
            <span>importado em {new Date(e.importacao.em).toLocaleString('pt-BR')}</span>
          </p>
          {e.cancelada ? (
            <p className="aviso aviso-erro" role="alert">
              Esta importação já foi cancelada em {new Date(e.cancelada.em).toLocaleString('pt-BR')}
              {e.cancelada.por ? ` por ${e.cancelada.por}` : ''}. Nada mais a fazer.
            </p>
          ) : (
            <>
              {bloqueado && (
                <div className="aviso aviso-erro" role="alert" data-teste="cancelamento-bloqueado-conciliados">
                  <strong>Cancelamento bloqueado:</strong> {plural(e.conciliados, 'movimento desta importação está conciliado', 'movimentos desta importação estão conciliados')}. Desfaça {e.conciliados > 1 ? 'essas conciliações' : 'essa conciliação'} na lista de movimentos (Ver conciliação › Desfazer) antes de cancelar a importação.
                  <ul className="conc-efeitos" data-teste="vinculos-conciliados">
                    {e.conciliadosLista.map((x) => (
                      <li key={x.numero}>
                        {x.numero} · {dataBr(x.data)} · {x.descricao} · {rs(x.valor)} — <strong>{x.conciliacao}</strong>
                        {x.vinculo ? ` com ${x.vinculo}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="aviso aviso-atencao-fin">
                Nada é apagado: a importação e os movimentos ficam no histórico como <strong>cancelados</strong>, com a justificativa e a auditoria. Os cancelados saem do saldo calculado e das pendências. O mesmo arquivo pode ser importado de novo depois.
              </p>
              <div className="conc-grade2">
                <div className="conc-caixa" data-teste="efeito-cancelados">
                  <h3>{plural(e.cancelados.length, 'movimento será cancelado', 'movimentos serão cancelados')}</h3>
                  <div>
                    Efeito no saldo calculado: <strong>{valorColorido(e.efeitoSaldoCalculado)}</strong>
                    {e.primeiraData ? ` a partir de ${dataBr(e.primeiraData)}` : ''}
                  </div>
                </div>
                <div className="conc-caixa" data-teste="efeito-mantidos">
                  <h3>
                    {plural(e.mantidos.length, 'movimento continua', 'movimentos continuam')}
                    <Ajuda titulo="Mantidos">Movimentos que também vieram em outro arquivo ativo (período sobreposto ou OFX × CSV) continuam valendo por esse outro arquivo. Só o vínculo com este arquivo deixa de contar.</Ajuda>
                  </h3>
                  <div className="texto-secundario">{e.mantidos.length ? `sustentados por ${[...new Set(e.mantidos.map((m) => m.sustentadoPor))].join(', ')}` : 'nenhum movimento desta importação aparece em outro arquivo ativo'}</div>
                </div>
              </div>
              {e.referencia?.usada && (
                <p className="aviso aviso-atencao-fin" data-teste="efeito-referencia">
                  O saldo do extrato desta importação deixa de ser usado na conferência de {mesExtenso(e.referencia.mes)}.{' '}
                  {e.referencia.substituta ? `Passa a valer o de ${e.referencia.substituta.numero} (${rs(e.referencia.substituta.valor)} em ${dataBr(e.referencia.substituta.data)}).` : 'O mês fica sem saldo do extrato para conferir até outra importação.'}
                </p>
              )}
              {(e.cancelados.length > 0 || e.mantidos.length > 0) && (
                <RolagemIndicada className="rolagem quadro-tabela">
                  <table className="tabela tabela-compacta" data-teste="movimentos-cancelamento">
                    <thead>
                      <tr>
                        <th>Movimento</th>
                        <th>Data</th>
                        <th>Descrição</th>
                        <th className="numero">Valor</th>
                        <th>Efeito</th>
                      </tr>
                    </thead>
                    <tbody>
                      {e.cancelados.map((m) => {
                        const conc = e.conciliadosLista.find((x) => x.numero === m.numero);
                        return (
                          <tr key={m.numero}>
                            <td className="conc-nowrap">{m.numero}</td>
                            <td>{dataBr(m.data)}</td>
                            <td>{m.descricao}</td>
                            <td className="numero">{valorColorido(m.valor)}</td>
                            <td>{conc ? <SeloSituacao texto={`Conciliado · ${conc.conciliacao} — impede`} tom="parcial" /> : <SeloSituacao texto="Será cancelado" tom="alerta" />}</td>
                          </tr>
                        );
                      })}
                      {e.mantidos.map((m) => (
                        <tr key={m.numero}>
                          <td className="conc-nowrap">{m.numero}</td>
                          <td>{dataBr(m.data)}</td>
                          <td>{m.descricao}</td>
                          <td className="numero">{valorColorido(m.valor)}</td>
                          <td>
                            <SeloSituacao texto={`Continua (${m.sustentadoPor})`} tom="positivo" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </RolagemIndicada>
              )}
              <div className="campo">
                <label htmlFor="justificativa-cancelamento">Justificativa (obrigatória, ao menos 10 caracteres)</label>
                <textarea id="justificativa-cancelamento" rows={2} maxLength={500} value={justificativa} onChange={(x) => setJustificativa(x.target.value)} disabled={bloqueado} data-teste="justificativa-cancelamento" />
              </div>
              <label className="conc-confirmar" data-teste="confirmar-cancelamento-caixa">
                <input type="checkbox" checked={confirmado} onChange={(x) => setConfirmado(x.target.checked)} disabled={bloqueado} />
                <span>
                  <strong>Confirmo o cancelamento de {numero}</strong>: {plural(e.cancelados.length, 'movimento deixa', 'movimentos deixam')} de contar no saldo calculado e nas pendências.
                </span>
              </label>
            </>
          )}
          {erro && (
            <p className="aviso aviso-erro" role="alert" data-teste="erro-cancelamento">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ---------- PDF do extrato: só evidência ---------- */
function AnexarPdf({ contaId, mes, aoFechar, aoAnexar }: { contaId: string; mes: string; aoFechar: () => void; aoAnexar: () => void }) {
  const [arquivo, setArquivo] = useState<{ nome: string; conteudo_base64: string } | null>(null);
  const [observacao, setObservacao] = useState('');
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const enviar = async () => {
    setEnviando(true);
    setErro('');
    try {
      await api.post('/api/conciliacao/evidencias', { conta_bancaria_id: contaId, mes, arquivo, observacao: observacao || undefined });
      aoAnexar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal
      titulo={`Anexar PDF do extrato — ${mesExtenso(mes)}`}
      aoFechar={aoFechar}
      medio
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!arquivo || enviando} onClick={() => void enviar()} data-teste="confirmar-pdf">
            Anexar PDF
          </button>
        </>
      }
    >
      <p className="texto-secundario">O PDF fica como evidência do extrato do mês. Nenhum dado é extraído dele.</p>
      <div className="campo">
        <label htmlFor="pdf-extrato">Arquivo PDF</label>
        <input id="pdf-extrato" type="file" accept=".pdf" onChange={async (e) => setArquivo((await arquivoParaApi(e.target.files?.[0] ?? null)) ?? null)} data-teste="arquivo-pdf" />
      </div>
      <div className="campo">
        <label htmlFor="obs-pdf">Observação (opcional)</label>
        <input id="obs-pdf" value={observacao} onChange={(e) => setObservacao(e.target.value)} maxLength={500} />
      </div>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
    </Modal>
  );
}

/* ====================================================================== B2 — conciliação */
interface PagamentoCand { numero: string; data: string; valor: string; meio: string; identificador: string | null; origem: string; descricao: string | null; favorecido: string | null; sugerido: boolean }
interface Candidatos {
  conta: { banco: string; agencia: string; numero: string };
  janelaDias: number;
  movimentos: { numero: string; data: string; valor: string; descricao: string; identificador: string | null }[];
  totalMovimentos: string;
  sugeridos: PagamentoCand[];
  outros: PagamentoCand[];
  busca: { de: string; ate: string; filtrou: boolean };
}
const msg = (e: unknown) => (e as Error).message;
const centavos = (v: string | number) => Math.round(Number(v) * 100);
/** Pagamento pela conta: o cartão da Inttrix não é meio de saída da conta. */
const MEIOS_CONTA = Object.entries(MEIOS).filter(([k]) => k !== 'cartao');
const ROTULO_NATUREZA: Record<string, string> = { devolucao_fornecedor: 'Devolução de fornecedor', aporte: 'Aporte', emprestimo: 'Empréstimo', rendimento: 'Rendimento da conta', estorno_banco: 'Estorno do banco', outra: 'Outra' };

/* ---------- C4 · C4-2 · C4-3: movimentos ⇄ pagamentos, soma conferida ---------- */
function ConciliarMovimentos({ movimentos, aoFechar, aoConciliar, aoRegistrar, aoVinculada, aoTransferencia, aoDevolucao }: { movimentos: string[]; aoFechar: () => void; aoConciliar: () => void; aoRegistrar: (mov: string) => void; aoVinculada: (mov: string) => void; aoTransferencia: (mov: string) => void; aoDevolucao: (movs: string[]) => void }) {
  const { pode } = useSessao();
  const [filtros, setFiltros] = useState({ busca: '', de: '', ate: '', valor: '' });
  const [aplicados, setAplicados] = useState(filtros);
  const cand = useCarga(() => api.get<Candidatos>(`/api/conciliacao/candidatos?movimentos=${encodeURIComponent(movimentos.join(','))}${aplicados.busca ? `&busca=${encodeURIComponent(aplicados.busca)}` : ''}${aplicados.de ? `&de=${aplicados.de}` : ''}${aplicados.ate ? `&ate=${aplicados.ate}` : ''}${aplicados.valor ? `&valor=${encodeURIComponent(aplicados.valor)}` : ''}`), [movimentos.join(','), aplicados]);
  const [marcados, setMarcados] = useState<Record<string, PagamentoCand>>({});
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const c = cand.dados;
  const lista = c ? [...c.sugeridos, ...c.outros.filter((p) => !c.sugeridos.some((s2) => s2.numero === p.numero))] : [];
  const extras = Object.values(marcados).filter((p) => !lista.some((x) => x.numero === p.numero));
  const totalMov = c ? centavos(c.totalMovimentos) : 0; // a API devolve o total das saídas em positivo
  const totalPg = Object.values(marcados).reduce((s2, p) => s2 + centavos(p.valor), 0);
  const dif = totalMov - totalPg;
  const pronto = c && Object.keys(marcados).length > 0 && dif === 0;
  const titulo = movimentos.length === 1 ? `Conciliar — 1 movimento ⇄ ${Object.keys(marcados).length > 1 ? `${Object.keys(marcados).length} pagamentos` : 'pagamento'}` : `Conciliar — ${movimentos.length} movimentos ⇄ ${Object.keys(marcados).length > 1 ? 'vários pagamentos' : 'pagamento'}`;
  const enviar = async () => {
    setEnviando(true);
    setErro('');
    try {
      const sugestao = Object.values(marcados).every((p) => p.sugerido);
      await api.post('/api/conciliacao/conciliacoes', { movimentos, pagamentos: Object.keys(marcados), origem: sugestao ? 'sugestao' : 'manual' });
      aoConciliar();
    } catch (e) {
      setErro(msg(e));
    } finally {
      setEnviando(false);
    }
  };
  const alternar = (p: PagamentoCand) => setMarcados((m) => (m[p.numero] ? Object.fromEntries(Object.entries(m).filter(([k]) => k !== p.numero)) : { ...m, [p.numero]: p }));
  const linhaPg = (p: PagamentoCand) => (
    <tr key={p.numero} data-teste={`pagamento-${p.numero}`}>
      <td className="col-selecao">
        <input type="checkbox" aria-label={`Selecionar ${p.numero}`} checked={Boolean(marcados[p.numero])} onChange={() => alternar(p)} data-teste={`marcar-${p.numero}`} />
      </td>
      <td className="conc-nowrap">
        <strong>{p.numero}</strong>
        {p.sugerido && <div><SeloSituacao texto="Sugerido" tom="positivo" /></div>}
      </td>
      <td className="conc-nowrap">{dataBr(p.data)}</td>
      <td>
        <span className="conc-origem">{p.origem}</span>
        <div className="texto-secundario conc-mini">{[p.favorecido, p.descricao].filter(Boolean).join(' · ')}</div>
      </td>
      <td className="numero conc-nowrap">{rs(p.valor)}</td>
    </tr>
  );
  return (
    <Modal
      titulo={titulo}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!pronto || enviando} onClick={() => void enviar()} data-teste="confirmar-conciliacao">
            Conciliar
          </button>
        </>
      }
    >
      {!c ? (
        <EstadoCarga carregando={cand.carregando} erro={cand.erro} recarregar={cand.recarregar} teste="candidatos" />
      ) : (
        <div data-teste="modal-conciliar">
          <div className="conc-empilhado">
            <div>
              <h3 className="conc-titulo">
                Movimentos do extrato
                <Ajuda titulo="Movimentos">{`Saídas da conta ${c.conta.banco} · ${c.conta.numero} escolhidas na lista. Para conciliar vários movimentos com um pagamento (ou vários com vários), marque-os na lista antes de abrir esta janela.`}</Ajuda>
              </h3>
              <RolagemIndicada className="rolagem quadro-tabela">
                <table className="tabela tabela-compacta" data-teste="movimentos-conciliar">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Descrição</th>
                      <th className="numero">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.movimentos.map((m) => (
                      <tr key={m.numero}>
                        <td className="conc-nowrap">{dataBr(m.data)}</td>
                        <td>
                          {m.descricao}
                          <div className="texto-secundario conc-mini">{m.numero}</div>
                        </td>
                        <td className="numero">{valorColorido(m.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </RolagemIndicada>
            </div>
            <div>
              <h3 className="conc-titulo">
                Pagamentos registrados
                <Ajuda titulo="Pagamentos">
                  {`Pagamentos pela conta da Inttrix (despesas, faturas e reembolsos) ainda não conciliados. “Sugerido”: mesmo valor e data até ${c.janelaDias} dias do movimento — nada é conciliado sozinho. Use a busca para outras datas ou valores. Compras no cartão e pagamentos feitos por pessoa não são saídas da conta e não aparecem aqui.`}
                </Ajuda>
              </h3>
              <div className="conc-busca" data-teste="busca-pagamentos">
                <input aria-label="Buscar pagamento" placeholder="PGT, despesa, fatura, favorecido…" value={filtros.busca} onChange={(e) => setFiltros({ ...filtros, busca: e.target.value })} data-teste="busca-texto" />
                <input aria-label="De" type="date" value={filtros.de} onChange={(e) => setFiltros({ ...filtros, de: e.target.value })} data-teste="busca-de" />
                <input aria-label="Até" type="date" value={filtros.ate} onChange={(e) => setFiltros({ ...filtros, ate: e.target.value })} data-teste="busca-ate" />
                <input aria-label="Valor" placeholder="Valor" value={filtros.valor} onChange={(e) => setFiltros({ ...filtros, valor: e.target.value })} data-teste="busca-valor" />
                <button type="button" className="botao-secundario pequeno" onClick={() => setAplicados(filtros)} data-teste="buscar-pagamentos">
                  Buscar
                </button>
              </div>
              <p className="texto-secundario conc-mini">
                {c.busca.filtrou ? 'Resultado da busca' : `Sugestões e pagamentos livres de ${dataBr(c.busca.de)} a ${dataBr(c.busca.ate)} (janela de ${c.janelaDias} dias)`}
              </p>
              {lista.length || extras.length ? (
                <RolagemIndicada className="rolagem quadro-tabela">
                  <table className="tabela tabela-compacta" data-teste="pagamentos-conciliar">
                    <thead>
                      <tr>
                        <th className="col-selecao" aria-label="Selecionar" />
                        <th>Pagamento</th>
                        <th>Data</th>
                        <th>Origem</th>
                        <th className="numero">Valor</th>
                      </tr>
                    </thead>
                    <tbody>{[...extras, ...lista].map(linhaPg)}</tbody>
                  </table>
                </RolagemIndicada>
              ) : (
                <p className="texto-secundario" data-teste="sem-candidatos">
                  Nenhum pagamento livre desta conta {c.busca.filtrou ? 'para esta busca' : 'na janela'}.
                </p>
              )}
            </div>
          </div>
          <p className="faixa-resumo-modal" data-teste="totais-conciliacao">
            <span>
              Movimentos selecionados <strong>{rs(totalMov / 100)}</strong>
            </span>
            <span>
              Pagamentos selecionados <strong>{rs(totalPg / 100)}</strong>
            </span>
            <span className={dif === 0 && totalPg > 0 ? 'conc-ok' : 'conc-dif'} data-teste="diferenca-conciliacao">
              Diferença {rs(Math.abs(dif) / 100)} {dif === 0 && totalPg > 0 ? '✓' : ''}
            </span>
          </p>
          {totalPg > 0 && dif !== 0 && (
            <p className="aviso aviso-erro" role="alert" data-teste="somas-nao-fecham">
              <strong>As somas não fecham.</strong> A conciliação só pode ser confirmada com diferença zero — ajuste a seleção.
            </p>
          )}
          {movimentos.length === 1 && (
            <p className="texto-secundario" data-teste="alternativas-conciliacao">
              Não há pagamento registrado?{' '}
              {pode('registrar', 'pagamento') && (
                <button type="button" className="botao-link" onClick={() => aoRegistrar(movimentos[0])} data-teste="abrir-registrar-conciliar">
                  Registrar pagamento e conciliar
                </button>
              )}
              {pode('registrar', 'pagamento') && pode('lancar', 'conta_pagar') && (
                <>
                  {' · '}
                  <button type="button" className="botao-link" onClick={() => aoVinculada(movimentos[0])} data-teste="abrir-despesa-vinculada">
                    Sem correspondência: lançar despesa já paga
                  </button>
                </>
              )}
              {' · '}
              <button type="button" className="botao-link" onClick={() => aoTransferencia(movimentos[0])} data-teste="abrir-transferencia">
                É transferência entre contas da Inttrix
              </button>
              {' · '}
              <button type="button" className="botao-link" onClick={() => aoDevolucao(movimentos)} data-teste="abrir-devolucao-credito">
                É devolução de crédito do pagador (Receber)
              </button>
              <Ajuda titulo="Sem pagamento registrado">“Registrar pagamento e conciliar” paga uma obrigação de despesa em aberto com os dados do movimento. “Sem correspondência” (tarifa, IOF, juros bancários…) lança a despesa já paga e a concilia. Em ambos, tudo é gravado numa operação só — se algo falhar, nada fica gravado. Se a saída foi para outra conta da Inttrix, vincule as duas pontas (sem pagamento nem despesa).</Ajuda>
            </p>
          )}
          {erro && (
            <p className="aviso aviso-erro" role="alert" data-teste="erro-conciliacao">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ---------- C5: registrar pagamento e conciliar (obrigação de despesa em aberto) ---------- */
interface ObrigacoesAbertas {
  movimento: { numero: string; data: string; valor: string; descricao: string; identificador: string | null };
  obrigacoes: { id: string; numero: string; despesa: string; descricao: string; favorecido: string | null; vencimento: string; valor: string; saldo: string }[];
}
function RegistrarEConciliar({ movimento, aoFechar, aoConcluir }: { movimento: string; aoFechar: () => void; aoConcluir: () => void }) {
  const [busca, setBusca] = useState('');
  const [aplicada, setAplicada] = useState('');
  const carga = useCarga(() => api.get<ObrigacoesAbertas>(`/api/conciliacao/obrigacoes-abertas?movimento=${movimento}${aplicada ? `&busca=${encodeURIComponent(aplicada)}` : ''}`), [movimento, aplicada]);
  const [escolhida, setEscolhida] = useState<string>('');
  const [meio, setMeio] = useState('pix');
  const [comprovante, setComprovante] = useState<{ nome: string; conteudo_base64: string } | null>(null);
  const [chave] = useState(novaChave);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const d = carga.dados;
  const o = d?.obrigacoes.find((x) => x.id === escolhida);
  const valor = d ? -centavos(d.movimento.valor) : 0;
  const saldo = o ? centavos(o.saldo) : 0;
  const acima = Boolean(o && valor > saldo);
  const enviar = async () => {
    setEnviando(true);
    setErro('');
    try {
      await api.post('/api/conciliacao/registrar-e-conciliar', { movimento, obrigacao_id: escolhida, meio, comprovante: comprovante ?? undefined, chave_idempotencia: chave });
      aoConcluir();
    } catch (e) {
      setErro(msg(e));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal
      titulo={`Registrar pagamento e conciliar${o ? ` — ${o.numero}` : ''}`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!o || acima || enviando} onClick={() => void enviar()} data-teste="confirmar-registrar-conciliar">
            Registrar pagamento e conciliar
          </button>
        </>
      }
    >
      {!d ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="obrigacoes-abertas" />
      ) : (
        <div data-teste="modal-registrar-conciliar">
          <p className="faixa-resumo-modal">
            <span>
              <strong>Movimento do extrato:</strong> {dataBr(d.movimento.data)} · {d.movimento.descricao} · {valorColorido(d.movimento.valor)}
              {d.movimento.identificador && <> · identificador {d.movimento.identificador}</>}
              <Ajuda titulo="Pagamento com os dados do extrato">O pagamento nasce com a data, o valor, a conta e o identificador deste movimento — os mesmos do banco — e já fica conciliado com ele. Faturas de cartão e reembolsos se pagam nas próprias telas; depois, concilie aqui.</Ajuda>
            </span>
          </p>
          <div className="conc-busca">
            <input aria-label="Buscar obrigação" placeholder="PAG-, descrição ou favorecido" value={busca} onChange={(e) => setBusca(e.target.value)} data-teste="busca-obrigacao" />
            <button type="button" className="botao-secundario pequeno" onClick={() => setAplicada(busca)}>
              Buscar
            </button>
          </div>
          {d.obrigacoes.length ? (
            <RolagemIndicada className="rolagem quadro-tabela">
              <table className="tabela tabela-compacta" data-teste="obrigacoes-abertas-lista">
                <thead>
                  <tr>
                    <th className="col-selecao" aria-label="Escolher" />
                    <th>Obrigação</th>
                    <th>Favorecido · descrição</th>
                    <th>Vencimento</th>
                    <th className="numero">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {d.obrigacoes.map((x) => (
                    <tr key={x.id}>
                      <td className="col-selecao">
                        <input type="radio" name="obrigacao" aria-label={`Escolher ${x.numero}`} checked={escolhida === x.id} onChange={() => setEscolhida(x.id)} data-teste={`obrigacao-${x.numero}`} />
                      </td>
                      <td className="conc-nowrap">
                        <strong>{x.numero}</strong>
                      </td>
                      <td>
                        {x.favorecido ?? '—'}
                        <div className="texto-secundario conc-mini">{x.descricao}</div>
                      </td>
                      <td className="conc-nowrap">{dataBr(x.vencimento)}</td>
                      <td className="numero conc-nowrap">{rs(x.saldo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </RolagemIndicada>
          ) : (
            <p className="texto-secundario">Nenhuma obrigação de despesa em aberto{aplicada ? ' para esta busca' : ''}.</p>
          )}
          <div className="grade-form tres">
            <Campo rotulo="Data do pagamento">{(id) => <input id={id} value={dataBr(d.movimento.data)} readOnly />}</Campo>
            <Campo rotulo="Valor pago">{(id) => <input id={id} value={rs(valor / 100)} readOnly />}</Campo>
            <Campo rotulo="Meio">
              {(id) => (
                <select id={id} value={meio} onChange={(e) => setMeio(e.target.value)} data-teste="meio-registrar">
                  {MEIOS_CONTA.map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              )}
            </Campo>
            <Campo rotulo="Identificador da transação">{(id) => <input id={id} value={d.movimento.identificador ? `${d.movimento.identificador} (do extrato)` : '—'} readOnly />}</Campo>
            <Campo rotulo="Comprovante (opcional)">{(id) => <input id={id} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={async (e) => setComprovante((await arquivoParaApi(e.target.files?.[0] ?? null)) ?? null)} />}</Campo>
          </div>
          {o && (
            <p className="faixa-resumo-modal" data-teste="resumo-registrar">
              <span>Pagamento {rs(valor / 100)}</span>
              <span>
                Aplicado {rs(Math.min(valor, saldo) / 100)} {!acima && '✓'}
              </span>
              <span>Saldo da obrigação depois {rs(Math.max(saldo - valor, 0) / 100)}</span>
            </p>
          )}
          {acima && (
            <p className="aviso aviso-atencao-fin" data-teste="acima-saldo">
              O movimento ({rs(valor / 100)}) passa o saldo de {o!.numero} ({rs(o!.saldo)}). Registre esse pagamento pelo Contas a Pagar (juros, multa ou crédito a recuperar) e depois concilie aqui.
            </p>
          )}
          {erro && (
            <p className="aviso aviso-erro" role="alert" data-teste="erro-registrar-conciliar">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ---------- sem correspondência (F16): despesa já paga vinculada ao movimento ---------- */
function DespesaVinculada({ movimento, aoFechar, aoConcluir }: { movimento: string; aoFechar: () => void; aoConcluir: () => void }) {
  const mov = useCarga(() => api.get<{ numero: string; data: string; valor: string; descricao: string; identificador: string | null; conta: string }>(`/api/conciliacao/movimentos/${movimento}`), [movimento]);
  const apoio = useApoio();
  const periodos = useCarga(() => api.get<{ mes: string; fechado: boolean }[]>('/api/financeiro/periodos').catch(() => []), []);
  const [favorecido, setFavorecido] = useState<Favorecido | null>(null);
  const [descricao, setDescricao] = useState('');
  const [plano, setPlano] = useState('');
  const [centro, setCentro] = useState('');
  const [meio, setMeio] = useState('debito_automatico');
  const [comp, setComp] = useState('');
  const [doc, setDoc] = useState<{ situacao: 'pendente' | 'confirmado'; tipo: string; numero: string; emissao: string }>({ situacao: 'pendente', tipo: '', numero: '', emissao: '' });
  const [chave] = useState(novaChave);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const m = mov.dados;
  const a = apoio.dados;
  useEffect(() => {
    if (m && !descricao) setDescricao(m.descricao.slice(0, 200));
    if (m && !comp) setComp(m.data.slice(0, 7));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m]);
  const mesMov = m?.data.slice(0, 7) ?? '';
  const compFechada = Boolean(comp && periodos.dados?.some((p) => p.mes === comp && p.fechado));
  const valor = m ? rs(Math.abs(Number(m.valor))) : '';
  const docOk = doc.situacao === 'pendente' || Boolean(doc.tipo);
  const enviar = async () => {
    setEnviando(true);
    setErro('');
    try {
      await api.post('/api/conciliacao/despesa-vinculada', {
        movimento,
        favorecido: favorecido ? { tipo: favorecido.tipo, id: favorecido.id } : undefined,
        descricao,
        competencia: comp,
        plano_contas_id: plano,
        centro_custo_id: centro || undefined,
        meio,
        documento: doc.situacao === 'confirmado' ? { situacao: 'confirmado', tipo: doc.tipo, numero: doc.numero || undefined, emissao: doc.emissao || undefined } : { situacao: 'pendente' },
        chave_idempotencia: chave,
      });
      aoConcluir();
    } catch (e) {
      setErro(msg(e));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal
      titulo="Sem correspondência — lançar despesa já paga e conciliar"
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button
            type="button"
            className="botao-primario"
            disabled={!favorecido || !plano || descricao.trim().length < 1 || !/^\d{4}-\d{2}$/.test(comp) || compFechada || !docOk || enviando}
            onClick={() => void enviar()}
            data-teste="confirmar-despesa-vinculada"
          >
            Lançar, pagar e conciliar
          </button>
        </>
      }
    >
      {!m || !a ? (
        <EstadoCarga carregando={mov.carregando || apoio.carregando} erro={mov.erro ?? apoio.erro} recarregar={() => (void mov.recarregar(), void apoio.recarregar())} teste="despesa-vinculada" />
      ) : (
        <div data-teste="modal-despesa-vinculada">
          <p className="faixa-resumo-modal">
            <span>
              <strong>Movimento do extrato:</strong> {dataBr(m.data)} · {m.descricao} · {valorColorido(m.valor)} · {m.conta}
              <Ajuda titulo="Despesa vinculada">Para saídas sem nenhum registro no HUB (tarifa, IOF, juros bancários…): a despesa nasce já paga e conciliada numa operação só. O pagamento usa a data efetiva, o valor, a conta e o identificador do extrato. A competência é a do resultado (sugerida pelo mês do movimento, editável).</Ajuda>
            </span>
          </p>
          <div className="grade-form duas">
            <div className="campo">
              <span className="rotulo-campo">Favorecido</span>
              <BuscaFavorecido valor={favorecido} aoEscolher={setFavorecido} />
            </div>
            <Campo rotulo="Descrição">{(id) => <input id={id} value={descricao} onChange={(e) => setDescricao(e.target.value)} maxLength={200} data-teste="descricao-vinculada" />}</Campo>
            <Campo rotulo="Conta gerencial">
              {(id) => (
                <select id={id} value={plano} onChange={(e) => setPlano(e.target.value)} data-teste="plano-vinculada">
                  <option value="">Escolha…</option>
                  {a.contasDespesa.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.codigo} — {c.nome}
                    </option>
                  ))}
                </select>
              )}
            </Campo>
            <Campo rotulo="Centro de custo (opcional)">
              {(id) => (
                <select id={id} value={centro} onChange={(e) => setCentro(e.target.value)}>
                  <option value="">—</option>
                  {a.centros.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.codigo} — {c.nome}
                    </option>
                  ))}
                </select>
              )}
            </Campo>
            <Campo rotulo="Competência">{(id) => <input id={id} type="month" value={comp} onChange={(e) => setComp(e.target.value)} data-teste="competencia-vinculada" />}</Campo>
            <Campo rotulo="Pagamento (do extrato)">{(id) => <input id={id} value={`${dataBr(m.data)} · ${valor}`} readOnly data-teste="pagamento-vinculada" />}</Campo>
            <Campo rotulo="Meio">
              {(id) => (
                <select id={id} value={meio} onChange={(e) => setMeio(e.target.value)} data-teste="meio-vinculada">
                  {MEIOS_CONTA.map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              )}
            </Campo>
          </div>
          {compFechada ? (
            <p className="aviso aviso-erro" role="alert" data-teste="competencia-fechada">
              A competência {mesExtenso(comp)} está fechada. Reabra o período (Contas a Pagar › Períodos financeiros) ou escolha outra competência — ela não é deslocada automaticamente.
            </p>
          ) : (
            comp &&
            comp !== mesMov && (
              <p className="aviso aviso-atencao-fin" data-teste="competencia-diferente">
                Competência {mesExtenso(comp)} diferente do mês do pagamento: a despesa entra no resultado de {mesExtenso(comp)}; o pagamento continua em {dataBr(m.data)}, a data do extrato.
              </p>
            )
          )}
          <fieldset className="conc-caixa" data-teste="documento-vinculada">
            <legend className="rotulo-campo">Documento</legend>
            <label className="conc-confirmar">
              <input type="radio" name="doc-vinculada" checked={doc.situacao === 'pendente'} onChange={() => setDoc({ ...doc, situacao: 'pendente' })} data-teste="documento-pendente" />
              <span>
                <strong>Pendente</strong> — complementar depois na própria despesa
              </span>
            </label>
            <label className="conc-confirmar">
              <input type="radio" name="doc-vinculada" checked={doc.situacao === 'confirmado'} onChange={() => setDoc({ ...doc, situacao: 'confirmado' })} data-teste="documento-recebido" />
              <span>
                <strong>Já tenho o documento</strong> (nota, recibo, boleto…)
              </span>
            </label>
            {doc.situacao === 'confirmado' ? (
              <div className="grade-form tres">
                <Campo rotulo="Tipo">
                  {(id) => (
                    <select id={id} value={doc.tipo} onChange={(e) => setDoc({ ...doc, tipo: e.target.value })} data-teste="tipo-documento-vinculada">
                      <option value="">Escolha…</option>
                      {Object.entries(TIPOS_DOCUMENTO).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </select>
                  )}
                </Campo>
                <Campo rotulo="Nº (opcional)">{(id) => <input id={id} value={doc.numero} onChange={(e) => setDoc({ ...doc, numero: e.target.value })} maxLength={60} />}</Campo>
                <Campo rotulo="Emissão (opcional)">{(id) => <input id={id} type="date" value={doc.emissao} onChange={(e) => setDoc({ ...doc, emissao: e.target.value })} />}</Campo>
              </div>
            ) : null}
            <p className="texto-secundario conc-mini" data-teste="explicacao-documento">
              {doc.situacao === 'pendente'
                ? `Pagamento confirmado pelo extrato (${valor} em ${dataBr(m.data)}). O documento fica pendente: a despesa nasce com o valor conhecido pelo extrato como estimado, já paga e conciliada. Ao completar depois em Contas a Pagar › ficha da despesa › Completar documento, a mesma despesa recebe o documento (sem outra despesa). Se o valor do documento for diferente, o Contas a Pagar trata a diferença: saldo a pagar ou crédito a recuperar, com justificativa — o pagamento do extrato não muda.`
                : `Pagamento confirmado pelo extrato e documento confirmado agora, com o valor do extrato (${valor}). Se o documento tiver outro valor, escolha "Pendente" e use depois "Completar documento" na ficha da despesa.`}
            </p>
          </fieldset>
          {erro && (
            <p className="aviso aviso-erro" role="alert" data-teste="erro-despesa-vinculada">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ---------- C7: movimento (entrada a identificar, aplicação/resgate, transferência) — observação e transferência ---------- */
interface MovimentoInfo { numero: string; data: string; valor: string; descricao: string; identificador: string | null; natureza: Natureza; conta: string; conciliacao: string | null; observacao: string | null; historico: { em: string; por: string | null; oque: string }[] }
function MovimentoDetalhe({ numero, aoFechar, aoAlterar }: { numero: string; aoFechar: () => void; aoAlterar: () => void }) {
  const { pode } = useSessao();
  const carga = useCarga(() => api.get<MovimentoInfo>(`/api/conciliacao/movimentos/${numero}`), [numero]);
  const m = carga.dados;
  const podeTransferir = Boolean(m && !m.conciliacao && m.natureza !== 'aplicacao_resgate' && pode('conciliar', 'conciliacao'));
  const pares = useCarga(
    () => (podeTransferir ? api.get<{ janelaDias: number; pares: { numero: string; data: string; valor: string; descricao: string; conta: string }[] }>(`/api/conciliacao/transferencia/pares?movimento=${numero}`) : Promise.resolve(null)),
    [numero, podeTransferir],
  );
  const [obs, setObs] = useState('');
  const [par, setPar] = useState('');
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  useEffect(() => {
    if (m) setObs(m.observacao ?? '');
  }, [m]);
  const salvar = async () => {
    setEnviando(true);
    setErro('');
    try {
      await api.post(`/api/conciliacao/movimentos/${numero}/observacao`, { observacao: obs });
      aoAlterar();
    } catch (e) {
      setErro(msg(e));
    } finally {
      setEnviando(false);
    }
  };
  const vincular = async () => {
    setEnviando(true);
    setErro('');
    try {
      await api.post('/api/conciliacao/transferencias', { movimentos: [numero, par] });
      aoAlterar();
    } catch (e) {
      setErro(msg(e));
    } finally {
      setEnviando(false);
    }
  };
  const titulo = m ? `Movimento do extrato — ${m.natureza === 'entrada' ? 'entrada' : m.natureza === 'aplicacao_resgate' ? 'aplicação/resgate' : m.natureza === 'transferencia' ? 'transferência' : 'saída'}` : 'Movimento do extrato';
  return (
    <Modal
      titulo={titulo}
      aoFechar={aoFechar}
      medio
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Fechar
          </button>
          {pode('conciliar', 'conciliacao') && (
            <button type="button" className="botao-primario" disabled={enviando || obs.trim().length < 1 || obs === (m?.observacao ?? '')} onClick={() => void salvar()} data-teste="salvar-observacao">
              Salvar observação
            </button>
          )}
        </>
      }
    >
      {!m ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="movimento-detalhe" />
      ) : (
        <div data-teste="modal-movimento">
          <p className="faixa-resumo-modal">
            <span>
              {dataBr(m.data)} · {m.descricao} · {valorColorido(m.valor)}
              <br />
              <span className="texto-secundario conc-mini">
                {m.identificador ? `identificador ${m.identificador}` : 'sem identificador do banco'} · {m.conta}
              </span>
            </span>
          </p>
          <p>
            {m.conciliacao ? <SeloSituacao texto={`Conciliado · ${m.conciliacao}`} tom="positivo" /> : <span className="conc-pend">{NATUREZA[m.natureza].rotulo}</span>}{' '}
            <Ajuda titulo="Situação">
              {m.natureza === 'entrada'
                ? 'Entrada a identificar: o dinheiro já está na conta e este movimento já participa do saldo bancário calculado. Ele permanece sem classificação e não é reconhecido automaticamente como receita; nenhum vínculo com o Receber é presumido. A futura identificação ou vinculação reaproveita este mesmo movimento, sem gerar outra entrada de dinheiro.'
                : m.natureza === 'aplicacao_resgate'
                  ? 'Aplicação/resgate: sem despesa nem receita. Concilie com a aplicação (APL) ou o resgate (RES) registrado em Aplicações — ou registre a partir deste movimento. O resgate nunca é presumido como só transferência, e o principal não é deduzido pelo líquido.'
                  : 'Transferência entre contas da Inttrix: sem receita nem despesa. Vincule as duas pontas quando ambas estiverem importadas.'}
            </Ajuda>
          </p>
          {m.natureza === 'entrada' && (
            <p className="texto-secundario" data-teste="orientacao-entrada">
              Já participa do saldo bancário calculado. Permanece sem classificação e não é reconhecido automaticamente como receita. A identificação futura reaproveita este movimento — não gera outra entrada de dinheiro.
            </p>
          )}
          <Campo rotulo="Observação (opcional)">{(id) => <textarea id={id} rows={2} maxLength={500} value={obs} onChange={(e) => setObs(e.target.value)} disabled={!pode('conciliar', 'conciliacao')} data-teste="observacao-movimento" />}</Campo>
          {podeTransferir && (
            <div className="conc-caixa" data-teste="transferencia-pares">
              <h3>
                Transferência entre contas da Inttrix
                <Ajuda titulo="Duas pontas">{`Só vincula com a outra ponta já importada em outra conta da Inttrix: mesmo valor, sinal oposto e até ${pares.dados?.janelaDias ?? 15} dias de diferença. Nenhum pagamento, despesa ou receita é criado; o caixa não é duplicado.`}</Ajuda>
              </h3>
              {!pares.dados ? (
                <EstadoCarga carregando={pares.carregando} erro={pares.erro} recarregar={pares.recarregar} teste="pares" />
              ) : pares.dados.pares.length ? (
                <>
                  {pares.dados.pares.map((x) => (
                    <label key={x.numero} className="conc-confirmar">
                      <input type="radio" name="par" checked={par === x.numero} onChange={() => setPar(x.numero)} data-teste={`par-${x.numero}`} />
                      <span>
                        {dataBr(x.data)} · {x.descricao} · {valorColorido(x.valor)}
                        <br />
                        <span className="texto-secundario conc-mini">
                          {x.numero} · {x.conta}
                        </span>
                      </span>
                    </label>
                  ))}
                  <button type="button" className="botao-secundario" disabled={!par || enviando} onClick={() => void vincular()} data-teste="vincular-transferencia">
                    Vincular as duas pontas
                  </button>
                </>
              ) : (
                <p className="texto-secundario" data-teste="sem-pares">
                  A outra ponta ainda não foi importada em outra conta da Inttrix (ou não confere em valor e data). O movimento segue pendente.
                </p>
              )}
            </div>
          )}
          {m.historico.length > 0 && <Historico itens={m.historico} />}
          {erro && (
            <p className="aviso aviso-erro" role="alert" data-teste="erro-movimento">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

function Historico({ itens }: { itens: { em: string; por: string | null; oque: string }[] }) {
  return (
    <>
      <h3 className="conc-titulo">Histórico deste movimento</h3>
      <RolagemIndicada className="rolagem quadro-tabela">
        <table className="tabela tabela-compacta" data-teste="historico-movimento">
          <thead>
            <tr>
              <th>Quando</th>
              <th>Quem</th>
              <th>O quê</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((h, i) => (
              <tr key={i}>
                <td className="conc-nowrap">{new Date(h.em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                <td>{h.por ?? '—'}</td>
                <td>{h.oque}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </RolagemIndicada>
    </>
  );
}

/* ---------- C6: desfazer conciliação (evento vinculado; pagamento e histórico preservados) ---------- */
interface ConciliacaoInfo {
  numero: string;
  tipo: 'pagamento' | 'transferencia' | 'receber' | 'recuperacao' | 'identificacao' | 'aplicacao';
  aplicacoes: { numero: string; tipo: 'apl' | 'res'; data: string; valor: string; aplicacao: string; descricao: string }[];
  recebimentos: { numero: string; data: string; valor: string; documento: string; registro: string | null }[];
  devolucoes: { credito: string; data: string; valor: string }[];
  recuperacoes: { numero: string; data: string; valor: string; obrigacao: string }[];
  identificacao: { natureza: string; descricao: string; pendenteVinculo: boolean; pagamentoRef: string | null; movimentoRef: string | null } | null;
  efeitoDesfazer: string;
  em: string;
  por: string | null;
  desfeita: { em: string; por: string | null; justificativa: string } | null;
  periodoFechado: boolean;
  movimentos: { numero: string; data: string; valor: string; descricao: string }[];
  pagamentos: { numero: string; data: string; valor: string; origem: string; descricao: string | null; favorecido: string | null }[];
}
function DesfazerConciliacao({ conciliacao, movimento, aoFechar, aoDesfazer, aoVincular }: { conciliacao: string; movimento: string; aoFechar: () => void; aoDesfazer: () => void; aoVincular: (identificacao: string) => void }) {
  const { pode } = useSessao();
  const carga = useCarga(() => Promise.all([api.get<ConciliacaoInfo>(`/api/conciliacao/conciliacoes/${conciliacao}`), api.get<MovimentoInfo>(`/api/conciliacao/movimentos/${movimento}`)]), [conciliacao, movimento]);
  const [just, setJust] = useState('');
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [c, m] = carga.dados ?? [null, null];
  const podeDesfazer = Boolean(c && !c.desfeita && !c.periodoFechado && pode('desfazer', 'conciliacao'));
  const enviar = async () => {
    setEnviando(true);
    setErro('');
    try {
      await api.post(`/api/conciliacao/conciliacoes/${conciliacao}/desfazer`, { justificativa: just });
      aoDesfazer();
    } catch (e) {
      setErro(msg(e));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal
      titulo={c && !c.desfeita ? 'Desfazer conciliação' : `Conciliação ${conciliacao}`}
      aoFechar={aoFechar}
      medio
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          {c && !c.desfeita && c.identificacao?.pendenteVinculo && pode('registrar', 'pagamento') && pode('desfazer', 'conciliacao') && pode('conciliar', 'conciliacao') && !c.periodoFechado && (
            <button type="button" className="botao-secundario" onClick={() => aoVincular(c.numero)} data-teste="vincular-credito">
              Vincular ao crédito a recuperar
            </button>
          )}
          {pode('desfazer', 'conciliacao') && c && !c.desfeita && (
            <button type="button" className="botao-primario" disabled={!podeDesfazer || just.trim().length < 10 || enviando} onClick={() => void enviar()} data-teste="confirmar-desfazer">
              Desfazer conciliação
            </button>
          )}
        </>
      }
    >
      {!c || !m ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="conciliacao-detalhe" />
      ) : (
        <div data-teste="modal-desfazer">
          <div className="faixa-resumo-modal conc-bloco">
            {c.movimentos.map((x) => (
              <div key={x.numero}>
                Movimento {dataBr(x.data)} · {x.descricao} · {valorColorido(x.valor)}
              </div>
            ))}
            <div data-teste="conciliacao-detalhe">
              ⇄{' '}
              {c.tipo === 'transferencia'
                ? 'transferência entre contas da Inttrix'
                : c.tipo === 'identificacao' && c.identificacao
                  ? `identificada: ${ROTULO_NATUREZA[c.identificacao.natureza] ?? c.identificacao.natureza} — “${c.identificacao.descricao}”${c.identificacao.pendenteVinculo ? ' · pendente de vínculo com crédito a recuperar' : ''}`
                  : c.tipo === 'receber'
                    ? [...c.recebimentos.map((r) => `${r.numero} (${r.documento}${r.registro ? ` · ${r.registro}` : ''})`), ...c.devolucoes.map((x) => `devolução do ${x.credito}`)].join(', ')
                    : c.tipo === 'recuperacao'
                      ? c.recuperacoes.map((r) => `${r.numero} (${r.obrigacao})`).join(', ')
                      : c.tipo === 'aplicacao'
                        ? c.aplicacoes.map((e) => `${e.numero} (${e.aplicacao} · ${e.descricao})`).join(', ')
                        : c.pagamentos.map((p) => `${p.numero} (${p.origem})`).join(', ')}{' '}
              · <strong>{c.numero}</strong>
            </div>
          </div>
          {c.desfeita ? (
            <p className="aviso aviso-atencao-fin">
              Desfeita em {new Date(c.desfeita.em).toLocaleString('pt-BR')}
              {c.desfeita.por ? ` por ${c.desfeita.por}` : ''} — “{c.desfeita.justificativa}”.
            </p>
          ) : (
            <>
              <ul className="conc-efeitos">
                {c.tipo === 'pagamento' && (
                  <li>
                    O <strong>pagamento continua registrado</strong> — desfazer não é estorno.
                  </li>
                )}
                {(c.tipo === 'receber' || c.tipo === 'recuperacao' || c.tipo === 'identificacao' || c.tipo === 'aplicacao') && <li data-teste="efeito-desfazer">{c.efeitoDesfazer}</li>}
                <li>
                  {c.movimentos.length > 1 ? 'Os movimentos voltam' : 'O movimento volta'} a <strong>pendente</strong>.
                </li>
                <li>O vínculo fica no histórico como desfeito, com a justificativa.</li>
              </ul>
              {c.periodoFechado && (
                <p className="aviso aviso-erro" role="alert" data-teste="desfazer-periodo-fechado">
                  O mês desta conciliação está fechado: desfazer exige reabrir o período.
                </p>
              )}
              {pode('desfazer', 'conciliacao') && (
                <Campo rotulo="Justificativa *">{(id) => <textarea id={id} rows={2} maxLength={500} value={just} onChange={(e) => setJust(e.target.value)} disabled={!podeDesfazer} placeholder="Ao menos 10 caracteres" data-teste="justificativa-desfazer" />}</Campo>
              )}
            </>
          )}
          {m.historico.length > 0 && <Historico itens={m.historico} />}
          {erro && (
            <p className="aviso aviso-erro" role="alert" data-teste="erro-desfazer">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
